/**
 * Restrained black chapter-end card: 第一章 / 南洋行 / —— 完 ——, then 保存 · 回到标题 · 重新开始.
 * The integrator calls this from ChapterEndScene.create.
 */
import Phaser from 'phaser';
import { CHAPTER_TITLE, FONT_DISPLAY, FONT_TITLE, GAME_H, GAME_W } from '@/core/config';
import { ctxOf } from '@/core/context';
import { ensureDevUiTextures } from '../devTextures';
import { MenuList } from '../MenuList';
import { uiText } from '../text';
import { Css, Palette } from '../theme';
import { uiRootOf } from '../UiRoot';

export interface ChapterEndOptions {
  /** Return false to report a failed save. */
  onSave: () => boolean | void;
  onTitle: () => void;
  onRestart: () => void;
  chapterTitle?: string;
  instant?: boolean;
}

export interface ChapterEndHandle { destroy(): void; readonly menu: MenuList; setStatus(text: string): void }

export function renderChapterEnd(scene: Phaser.Scene, opts: ChapterEndOptions): ChapterEndHandle {
  ensureDevUiTextures(scene);
  const ctx = ctxOf(scene);
  const reduce = (ctx?.settings.reduceMotion ?? false) || Boolean(opts.instant);
  scene.cameras.main.setBackgroundColor(0x05070a);
  const objects: Phaser.GameObjects.GameObject[] = [];
  const keep = <T extends Phaser.GameObjects.GameObject>(o: T): T => { objects.push(o); return o; };

  const chapter = keep(uiText(scene, GAME_W / 2, 96, '第一章', { fontFamily: FONT_TITLE, fontSize: '14px', fontStyle: 'bold', color: Css.grey, letterSpacing: 4 }).setOrigin(0.5));
  const title = keep(uiText(scene, GAME_W / 2, 136, opts.chapterTitle ?? CHAPTER_TITLE, { fontFamily: FONT_DISPLAY, fontSize: '42px', color: Css.sand, letterSpacing: 8 }).setOrigin(0.5));
  const rule = keep(scene.add.rectangle(GAME_W / 2, 168, 120, 1, Palette.sand, 0.5).setOrigin(0.5));
  const fin = keep(uiText(scene, GAME_W / 2, 184, '—— 完 ——', { fontFamily: FONT_TITLE, fontSize: '13px', color: Css.grey }).setOrigin(0.5));
  const status = keep(uiText(scene, GAME_W / 2, GAME_H - 28, '', { fontSize: '12px', color: Css.sand }).setOrigin(0.5));

  let ready = reduce;
  const isBlocked = () => !ready || Boolean(uiRootOf(scene)?.hasModal());
  const menu = new MenuList(scene, {
    x: GAME_W / 2, y: 236, width: 180, rowHeight: 26, fontSize: 16, isBlocked, depth: 5, color: Css.parchment, selectedColor: Css.white,
    items: [
      { label: '保存', onSelect: () => { const ok = opts.onSave(); status.setText(ok === false ? '保存失败' : '已保存 · 存档一'); } },
      { label: '回到标题', onSelect: () => opts.onTitle() },
      { label: '重新开始', onSelect: () => opts.onRestart() },
    ],
  });

  if (!reduce) {
    const seq: [Phaser.GameObjects.Components.AlphaSingle, number][] = [[chapter, 200], [title, 500], [rule, 900], [fin, 1100], [menu.container, 1500]];
    for (const [o, delay] of seq) { o.setAlpha(0); scene.tweens.add({ targets: o, alpha: 1, duration: 600, delay, ease: 'Sine.easeOut' }); }
    scene.time.delayedCall(1500, () => { ready = true; });
  }

  const handle: ChapterEndHandle = {
    menu,
    setStatus: (t) => status.setText(t),
    destroy: () => { menu.destroy(); for (const o of objects) o.destroy(); objects.length = 0; },
  };
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => handle.destroy());
  return handle;
}
